import { useState, useCallback } from 'react';
import { makeStyles, tokens, shorthands } from '@fluentui/react-components';
import { fontScale } from '@/theme';
import { SpaceCard } from '@components/spaces/SpaceCard/SpaceCard';
import type { SpaceWithAvailability } from '@store/models';
import type { BuildingId, SpaceId } from '@store/models/ids';

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------
const useDemoStyles = makeStyles({
  page: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.padding('2rem', '1.5rem'),
    gap: '2.5rem',
    maxWidth: '75rem',
    marginLeft: 'auto',
    marginRight: 'auto',
    boxSizing: 'border-box',
  },
  pageTitle: {
    fontWeight: 700,
    fontSize: fontScale.title,
    color: tokens.colorNeutralForeground1,
    marginBottom: '0.25rem',
  },
  pageSubtitle: {
    fontWeight: 400,
    fontSize: fontScale.body,
    color: tokens.colorNeutralForeground2,
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  sectionHeading: {
    fontWeight: 600,
    fontSize: fontScale.subtitle,
    color: tokens.colorNeutralForeground1,
    ...shorthands.borderBottom('0.0625rem', 'solid', tokens.colorNeutralStroke2),
    paddingBottom: '0.5rem',
  },
  sectionDescription: {
    fontWeight: 400,
    fontSize: fontScale.body,
    color: tokens.colorNeutralForeground3,
    marginTop: '-0.5rem',
  },
  grid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1.5rem',
  },
  singleColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1.5rem',
    maxWidth: '40rem',
  },
  logBox: {
    fontFamily: 'monospace',
    fontSize: fontScale.caption,
    color: tokens.colorNeutralForeground2,
    backgroundColor: tokens.colorNeutralBackground3,
    ...shorthands.borderRadius('0.5rem'),
    ...shorthands.padding('0.75rem', '1rem'),
    maxHeight: '7.5rem',
    overflowY: 'auto',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
  },
});

// ---------------------------------------------------------------------------
// Mock space factory
// ---------------------------------------------------------------------------
const makeSpace = (
  overrides: Omit<Partial<SpaceWithAvailability>, 'spaceId'> & {
    id: string;
    spaceId: string;
    spaceName: string;
  }
): SpaceWithAvailability => ({
  buildingId: 'bld-atl-170' as BuildingId,
  spaceEmail: 'room@contoso.com',
  floor: '1.2A',
  roomNumber: '1',
  roomTypeCode: 'CONF',
  roomCapacity: 18,
  isHubRoom: false,
  isComboSpace: false,
  isBoardRoom: false,
  amenityCodes: [
    'mountedMonitor',
    'laptopConnectionDongles',
    'teamsEnabled',
    'powerOutlets',
    'whiteboard',
    'adjustableChairs',
    'flooringToCeilingWindows',
  ],
  supportedRoomLayouts: [],
  isActive: true,
  createdAt: '2025-01-01T00:00:00Z',
  lastUpdatedAt: '2025-01-01T00:00:00Z',
  ...overrides,
  spaceId: overrides.spaceId as SpaceId,
});

/** Helper — returns selected setups for a space, defaulting to empty array */
const getSetups = (setups: Record<string, string[]>, spaceId: string): string[] =>
  setups[spaceId] ?? [];

// ---------------------------------------------------------------------------
// Pre-built demo spaces representing every meaningful state
// ---------------------------------------------------------------------------
const DEMO_SPACES = {
  available: makeSpace({
    id: 'demo-available',
    spaceId: 'sp-demo-1',
    spaceName: 'Conf Room ATL-AY-NORTH/1.2A (8-14)',
    roomCapacity: 18,
    floor: '1.2A',
    roomNumber: '1',
  }),

  recommended: makeSpace({
    id: 'demo-recommended',
    spaceId: 'sp-demo-2',
    spaceName: 'Conf Room ATL-AY-NORTH/1.2B (16-20)',
    roomCapacity: 14,
    floor: '1.2B',
    roomNumber: '1',
  }),

  booked: makeSpace({
    id: 'demo-booked',
    spaceId: 'sp-demo-3',
    spaceName: 'Conf Room ATL-AY-NORTH/1.2C (24-32)',
    spaceAvailability: [
      {
        dayStartTime: '2025-01-01T08:00:00Z',
        dayEndTime: '2025-01-01T17:00:00Z',
        timeZone: 'UTC',
        available: false,
      },
    ],
    roomCapacity: 32,
    floor: '1.2C',
    roomNumber: '1',
    amenityCodes: [
      'mountedMonitor',
      'laptopConnectionDongles',
      'teamsEnabled',
      'powerOutlets',
      'whiteboard',
      'cateringCredenza',
      'adjustableChairs',
      'flooringToCeilingWindows',
    ],
    supportedRoomLayouts: [{ code: 'U-Shaped setup', capacity: 22 }],
  }),

  selected: makeSpace({
    id: 'demo-selected',
    spaceId: 'sp-demo-4',
    spaceName: 'Redmond Hub — Selected',
  }),

  selectedRecommended: makeSpace({
    id: 'demo-sel-rec',
    spaceId: 'sp-demo-5',
    spaceName: 'Executive Suite — Selected + Recommended',
    isBoardRoom: true,
  }),

  capacityWarning: makeSpace({
    id: 'demo-capacity',
    spaceId: 'sp-demo-6',
    spaceName: 'Compact Studio — Capacity Warning',
    roomCapacity: 20,
    floor: '1',
    roomNumber: 'A-105',
    amenityCodes: ['av', 'videoConference', 'whiteboard'],
    supportedRoomLayouts: [
      { code: 'Conference room style', capacity: 20 },
      { code: 'U-Shaped setup', capacity: 15 },
    ],
  }),

  noRoomLayouts: makeSpace({
    id: 'demo-no-layouts',
    spaceId: 'sp-demo-7',
    spaceName: 'Focus Room — No Room Preferences',
    roomCapacity: 8,
    floor: '2',
    roomNumber: 'B-210',
    amenityCodes: ['av', 'videoConference', 'teams'],
    supportedRoomLayouts: [],
  }),

  noAmenities: makeSpace({
    id: 'demo-no-amenities',
    spaceId: 'sp-demo-8',
    spaceName: 'Flex Space — No Amenities',
    roomCapacity: 40,
    floor: '1',
    roomNumber: 'A-110',
    amenityCodes: [],
    supportedRoomLayouts: [
      { code: 'Classroom style', capacity: 30 },
      { code: 'Auditorium setup', capacity: 40 },
    ],
  }),

  minimal: makeSpace({
    id: 'demo-minimal',
    spaceId: 'sp-demo-9',
    spaceName: 'Huddle Pod — Minimal Config',
    roomCapacity: 4,
    floor: 'G',
    roomNumber: 'G-02',
    amenityCodes: ['teams'],
    supportedRoomLayouts: [],
  }),

  singleLayout: makeSpace({
    id: 'demo-single-layout',
    spaceId: 'sp-demo-10',
    spaceName: 'Board Room — Single Layout',
    roomCapacity: 16,
    floor: '5',
    roomNumber: 'E-500',
    isBoardRoom: true,
    amenityCodes: ['av', 'videoConference', 'teams', 'whiteboard', 'microphone'],
    supportedRoomLayouts: [{ code: 'Conference room style', capacity: 16 }],
  }),

  boardroomExecutive: makeSpace({
    id: 'demo-boardroom-exec',
    spaceId: 'sp-demo-boardroom-1',
    spaceName: 'Executive Boardroom — NYC-300 Lafayette',
    roomCapacity: 24,
    floor: '6A01',
    roomNumber: '6',
    isBoardRoom: true,
    amenityCodes: [
      'OVERHEAD-PROJECTION-SYSTEM',
      'LAPTOP-CONNECTION-DONGLES',
      'TEAMS-ENABLED',
      'CAMERAS',
      'CEILING-MICS',
      'PODIUM',
    ],
    supportedRoomLayouts: [{ code: 'boardroom', capacity: 24 }],
  }),

  nonBoardroomMultiLayout: makeSpace({
    id: 'demo-non-boardroom',
    spaceId: 'sp-demo-non-boardroom-1',
    spaceName: 'Multipurpose Meeting Room — NYC-300',
    roomCapacity: 60,
    floor: '3',
    roomNumber: 'C-301',
    isBoardRoom: false,
    amenityCodes: ['av', 'videoConference', 'teams', 'whiteboard', 'microphone', 'catering'],
    supportedRoomLayouts: [
      { code: 'Classroom style', capacity: 40 },
      { code: 'Conference room style', capacity: 30 },
      { code: 'U-Shaped setup', capacity: 25 },
      { code: 'Auditorium setup', capacity: 60 },
    ],
  }),

  partialOneDay: makeSpace({
    id: 'demo-partial-1',
    spaceId: 'sp-demo-11',
    spaceName: 'Innovation Hub — Large Conference Room',
    roomCapacity: 60,
    floor: '3',
    roomNumber: 'C-301',
    amenityCodes: ['av', 'videoConference', 'teams', 'whiteboard', 'microphone', 'catering'],
    supportedRoomLayouts: [
      { code: 'Classroom style', capacity: 40 },
      { code: 'Conference room style', capacity: 30 },
    ],
    spaceAvailability: [
      {
        dayStartTime: '2026-04-15T09:00:00',
        dayEndTime: '2026-04-15T17:00:00',
        timeZone: 'Eastern Standard Time',
        available: true,
      },
      {
        dayStartTime: '2026-04-16T09:00:00',
        dayEndTime: '2026-04-16T17:00:00',
        timeZone: 'Eastern Standard Time',
        available: false,
      },
      {
        dayStartTime: '2026-04-17T09:00:00',
        dayEndTime: '2026-04-17T17:00:00',
        timeZone: 'Eastern Standard Time',
        available: false,
      },
    ],
  }),

  partialMultipleDays: makeSpace({
    id: 'demo-partial-2',
    spaceId: 'sp-demo-12',
    spaceName: 'Conf Room ATL-AY-SOUTH/2.1A (12-18)',
    roomCapacity: 24,
    floor: '2.1A',
    roomNumber: '2',
    spaceAvailability: [
      {
        dayStartTime: '2026-04-15T09:00:00',
        dayEndTime: '2026-04-15T17:00:00',
        timeZone: 'Eastern Standard Time',
        available: true,
      },
      {
        dayStartTime: '2026-04-16T09:00:00',
        dayEndTime: '2026-04-16T17:00:00',
        timeZone: 'Eastern Standard Time',
        available: false,
      },
      {
        dayStartTime: '2026-04-17T09:00:00',
        dayEndTime: '2026-04-17T17:00:00',
        timeZone: 'Eastern Standard Time',
        available: true,
      },
      {
        dayStartTime: '2026-04-18T09:00:00',
        dayEndTime: '2026-04-18T17:00:00',
        timeZone: 'Eastern Standard Time',
        available: false,
      },
    ],
  }),

  partialThreeDays: makeSpace({
    id: 'demo-partial-3',
    spaceId: 'sp-demo-14',
    spaceName: 'Atlanta Event Hall — 3 of 5 days available',
    roomCapacity: 120,
    floor: 'G',
    roomNumber: 'G-01',
    amenityCodes: [
      'av',
      'videoConference',
      'teams',
      'whiteboard',
      'microphone',
      'catering',
      'adjustableChairs',
    ],
    supportedRoomLayouts: [
      { code: 'Auditorium setup', capacity: 120 },
      { code: 'Classroom style', capacity: 80 },
    ],
    spaceAvailability: [
      {
        dayStartTime: '2026-04-14T09:00:00',
        dayEndTime: '2026-04-14T17:00:00',
        timeZone: 'Eastern Standard Time',
        available: true,
      },
      {
        dayStartTime: '2026-04-15T09:00:00',
        dayEndTime: '2026-04-15T17:00:00',
        timeZone: 'Eastern Standard Time',
        available: false,
      },
      {
        dayStartTime: '2026-04-16T09:00:00',
        dayEndTime: '2026-04-16T17:00:00',
        timeZone: 'Eastern Standard Time',
        available: true,
      },
      {
        dayStartTime: '2026-04-17T09:00:00',
        dayEndTime: '2026-04-17T17:00:00',
        timeZone: 'Eastern Standard Time',
        available: false,
      },
      {
        dayStartTime: '2026-04-18T09:00:00',
        dayEndTime: '2026-04-18T17:00:00',
        timeZone: 'Eastern Standard Time',
        available: true,
      },
    ],
  }),

  fullyAvailableWithData: makeSpace({
    id: 'demo-full-avail',
    spaceId: 'sp-demo-13',
    spaceName: 'Conf Room ATL-AY-NORTH/3.1A — Fully Available',
    roomCapacity: 30,
    floor: '3.1A',
    roomNumber: '1',
    spaceAvailability: [
      {
        dayStartTime: '2026-04-15T09:00:00',
        dayEndTime: '2026-04-15T17:00:00',
        timeZone: 'Eastern Standard Time',
        available: true,
      },
      {
        dayStartTime: '2026-04-16T09:00:00',
        dayEndTime: '2026-04-16T17:00:00',
        timeZone: 'Eastern Standard Time',
        available: true,
      },
      {
        dayStartTime: '2026-04-17T09:00:00',
        dayEndTime: '2026-04-17T17:00:00',
        timeZone: 'Eastern Standard Time',
        available: true,
      },
    ],
  }),
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export const SpaceCardDemo = () => {
  const styles = useDemoStyles();

  // Track selections & setups per-space so the demo is interactive
  const [selectedIds, setSelectedIds] = useState<Set<string>>(
    new Set([DEMO_SPACES.selected.spaceId, DEMO_SPACES.selectedRecommended.spaceId])
  );
  const [setups, setSetups] = useState<Record<string, string[]>>({});
  const [log, setLog] = useState<string[]>([]);

  const pushLog = useCallback((msg: string) => {
    setLog((prev) => [`[${new Date().toLocaleTimeString()}] ${msg}`, ...prev].slice(0, 50));
  }, []);

  const handleSelect = useCallback(
    (spaceId: string) => {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (next.has(spaceId)) {
          next.delete(spaceId);
          pushLog(`Deselected → ${spaceId}`);
        } else {
          next.add(spaceId);
          pushLog(`Selected → ${spaceId}`);
        }
        return next;
      });
    },
    [pushLog]
  );

  const handleSetupChange = useCallback(
    (spaceId: string, codes: string[]) => {
      setSetups((prev) => ({ ...prev, [spaceId]: codes }));
      pushLog(`Setup changed → ${spaceId}: [${codes.join(', ')}]`);
    },
    [pushLog]
  );

  return (
    <div className={styles.page}>
      {/* Header */}
      <div>
        <div className={styles.pageTitle}>SpaceCard — Demo Gallery</div>
        <div className={styles.pageSubtitle}>
          All supported configurations and visual states of the SpaceCard component. Cards are
          interactive — click to select/deselect and expand room preferences.
        </div>
      </div>

      {/* ---------------------------------------------------------------- */}
      {/* 1. Core States */}
      {/* ---------------------------------------------------------------- */}
      <div className={styles.section}>
        <div className={styles.sectionHeading}>Core States</div>
        <div className={styles.sectionDescription}>
          Available, Recommended, Selected, and Booked — the four primary visual states.
        </div>
        <div className={styles.grid}>
          {/* Available */}
          <SpaceCard
            space={DEMO_SPACES.available}
            selected={selectedIds.has(DEMO_SPACES.available.spaceId)}
            onSelect={handleSelect}
            selectedSetups={getSetups(setups, DEMO_SPACES.available.spaceId)}
            onSetupChange={handleSetupChange}
          />

          {/* Recommended */}
          <SpaceCard
            space={DEMO_SPACES.recommended}
            isRecommended
            selected={selectedIds.has(DEMO_SPACES.recommended.spaceId)}
            onSelect={handleSelect}
            selectedSetups={getSetups(setups, DEMO_SPACES.recommended.spaceId)}
            onSetupChange={handleSetupChange}
          />

          {/* Selected */}
          <SpaceCard
            space={DEMO_SPACES.selected}
            selected={selectedIds.has(DEMO_SPACES.selected.spaceId)}
            onSelect={handleSelect}
            selectedSetups={getSetups(setups, DEMO_SPACES.selected.spaceId)}
            onSetupChange={handleSetupChange}
          />

          {/* Booked (disabled) */}
          <SpaceCard space={DEMO_SPACES.booked} selected={false} onSelect={handleSelect} />
        </div>
      </div>

      {/* ---------------------------------------------------------------- */}
      {/* 2. Combined States */}
      {/* ---------------------------------------------------------------- */}
      <div className={styles.section}>
        <div className={styles.sectionHeading}>Combined States</div>
        <div className={styles.sectionDescription}>
          Selected + Recommended badge shown simultaneously.
        </div>
        <div className={styles.grid}>
          <SpaceCard
            space={DEMO_SPACES.selectedRecommended}
            isRecommended
            selected={selectedIds.has(DEMO_SPACES.selectedRecommended.spaceId)}
            onSelect={handleSelect}
            selectedSetups={getSetups(setups, DEMO_SPACES.selectedRecommended.spaceId)}
            onSetupChange={handleSetupChange}
          />
        </div>
      </div>

      {/* ---------------------------------------------------------------- */}
      {/* 3. Capacity Warning */}
      {/* ---------------------------------------------------------------- */}
      <div className={styles.section}>
        <div className={styles.sectionHeading}>Capacity Warning</div>
        <div className={styles.sectionDescription}>
          When attendee count exceeds room capacity, a warning banner appears inside the expanded
          room preferences area.
        </div>
        <div className={styles.singleColumn}>
          <SpaceCard
            space={DEMO_SPACES.capacityWarning}
            attendeeCount={35}
            selected={selectedIds.has(DEMO_SPACES.capacityWarning.spaceId)}
            onSelect={handleSelect}
            selectedSetups={getSetups(setups, DEMO_SPACES.capacityWarning.spaceId)}
            onSetupChange={handleSetupChange}
          />
        </div>
      </div>

      {/* ---------------------------------------------------------------- */}
      {/* 4. Edge: No Room Layouts */}
      {/* ---------------------------------------------------------------- */}
      <div className={styles.section}>
        <div className={styles.sectionHeading}>No Room Preferences</div>
        <div className={styles.sectionDescription}>
          Spaces with no supported room layouts hide the collapsible toggle and show only the select
          button.
        </div>
        <div className={styles.grid}>
          <SpaceCard
            space={DEMO_SPACES.noRoomLayouts}
            selected={selectedIds.has(DEMO_SPACES.noRoomLayouts.spaceId)}
            onSelect={handleSelect}
          />
        </div>
      </div>

      {/* ---------------------------------------------------------------- */}
      {/* 5. Edge: No Amenities */}
      {/* ---------------------------------------------------------------- */}
      <div className={styles.section}>
        <div className={styles.sectionHeading}>No Amenities</div>
        <div className={styles.sectionDescription}>
          Card renders cleanly when there are zero amenity codes — no pill row is shown.
        </div>
        <div className={styles.grid}>
          <SpaceCard
            space={DEMO_SPACES.noAmenities}
            selected={selectedIds.has(DEMO_SPACES.noAmenities.spaceId)}
            onSelect={handleSelect}
            selectedSetups={getSetups(setups, DEMO_SPACES.noAmenities.spaceId)}
            onSetupChange={handleSetupChange}
          />
        </div>
      </div>

      {/* ---------------------------------------------------------------- */}
      {/* 6. Minimal / Small Room */}
      {/* ---------------------------------------------------------------- */}
      <div className={styles.section}>
        <div className={styles.sectionHeading}>Minimal Configuration</div>
        <div className={styles.sectionDescription}>
          Smallest possible card — 1 amenity, no room layouts, low capacity.
        </div>
        <div className={styles.grid}>
          <SpaceCard
            space={DEMO_SPACES.minimal}
            selected={selectedIds.has(DEMO_SPACES.minimal.spaceId)}
            onSelect={handleSelect}
          />
        </div>
      </div>

      {/* ---------------------------------------------------------------- */}
      {/* 7. Single Room Layout */}
      {/* ---------------------------------------------------------------- */}
      <div className={styles.section}>
        <div className={styles.sectionHeading}>Single Room Layout</div>
        <div className={styles.sectionDescription}>
          Room preference toggle with only one layout option available.
        </div>
        <div className={styles.grid}>
          <SpaceCard
            space={DEMO_SPACES.singleLayout}
            selected={selectedIds.has(DEMO_SPACES.singleLayout.spaceId)}
            onSelect={handleSelect}
            selectedSetups={getSetups(setups, DEMO_SPACES.singleLayout.spaceId)}
            onSetupChange={handleSetupChange}
          />
        </div>
      </div>

      {/* ---------------------------------------------------------------- */}
      {/* 7b. Boardroom vs Non-Boardroom */}
      {/* ---------------------------------------------------------------- */}
      <div className={styles.section}>
        <div className={styles.sectionHeading}>Boardroom vs Non-Boardroom Setup</div>
        <div className={styles.sectionDescription}>
          When a space has isBoardRoom=true, the room preference area shows a single fixed
          &ldquo;Boardroom&rdquo; card that is pre-selected and disabled. Non-boardroom spaces show
          all available layout options for selection.
        </div>
        <div className={styles.grid}>
          <SpaceCard
            space={DEMO_SPACES.boardroomExecutive}
            selected={selectedIds.has(DEMO_SPACES.boardroomExecutive.spaceId)}
            onSelect={handleSelect}
          />
          <SpaceCard
            space={DEMO_SPACES.nonBoardroomMultiLayout}
            selected={selectedIds.has(DEMO_SPACES.nonBoardroomMultiLayout.spaceId)}
            onSelect={handleSelect}
            selectedSetups={getSetups(setups, DEMO_SPACES.nonBoardroomMultiLayout.spaceId)}
            onSetupChange={handleSetupChange}
          />
        </div>
      </div>

      {/* ---------------------------------------------------------------- */}
      {/* 8. Partial Availability (Tooltip) */}
      {/* ---------------------------------------------------------------- */}
      <div className={styles.section}>
        <div className={styles.sectionHeading}>Partial Availability — Badge Tooltip</div>
        <div className={styles.sectionDescription}>
          When a space is partially available, the badge shows &ldquo;Available for N day(s)&rdquo;
          with an info icon. Hover over the badge to see the tooltip with specific available dates.
        </div>
        <div className={styles.grid}>
          <SpaceCard
            space={DEMO_SPACES.partialOneDay}
            selected={selectedIds.has(DEMO_SPACES.partialOneDay.spaceId)}
            onSelect={handleSelect}
            selectedSetups={getSetups(setups, DEMO_SPACES.partialOneDay.spaceId)}
            onSetupChange={handleSetupChange}
          />
          <SpaceCard
            space={DEMO_SPACES.partialMultipleDays}
            selected={selectedIds.has(DEMO_SPACES.partialMultipleDays.spaceId)}
            onSelect={handleSelect}
            selectedSetups={getSetups(setups, DEMO_SPACES.partialMultipleDays.spaceId)}
            onSetupChange={handleSetupChange}
          />
          <SpaceCard
            space={DEMO_SPACES.partialThreeDays}
            selected={selectedIds.has(DEMO_SPACES.partialThreeDays.spaceId)}
            onSelect={handleSelect}
            selectedSetups={getSetups(setups, DEMO_SPACES.partialThreeDays.spaceId)}
            onSetupChange={handleSetupChange}
          />
          <SpaceCard
            space={DEMO_SPACES.fullyAvailableWithData}
            selected={selectedIds.has(DEMO_SPACES.fullyAvailableWithData.spaceId)}
            onSelect={handleSelect}
            selectedSetups={getSetups(setups, DEMO_SPACES.fullyAvailableWithData.spaceId)}
            onSetupChange={handleSetupChange}
          />
        </div>
      </div>

      {/* ---------------------------------------------------------------- */}
      {/* Event Log */}
      {/* ---------------------------------------------------------------- */}
      <div className={styles.section}>
        <div className={styles.sectionHeading}>Event Log</div>
        <div className={styles.logBox}>
          {log.length === 0
            ? 'Interact with the cards above — events will appear here.'
            : log.join('\n')}
        </div>
      </div>
    </div>
  );
};

SpaceCardDemo.displayName = 'SpaceCardDemo';
